import { PageHeader } from '@/components/PageHeader/PageHeader'
import { useCompany } from '@/contexts'
import { CreateVehicleFormSchema, type CreateVehicleForm } from '@/features/vehicles/api'
import { VehicleForm } from '@/features/vehicles/components/layout/VehicleForm'
import { useVehicle } from '@/features/vehicles/hooks'
import {
  useUpdateVehicle,
  type UseUpdateVehicleOptions
} from '@/features/vehicles/hooks/useUpdateVehicle'
import { useMessageSnackbar } from '@/hooks/useMessageSnackbar'
import { VehicleTypesSchema } from '@/types/vehicle.types.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Container } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_authenticated/$companySlug/vehicles/$vin/update')(
  {
    beforeLoad: ({ context }) => {
      context.auth.rejectIfMissingPermissions(['VEHICLE_READ', 'VEHICLE_WRITE'])
    },
    component: UpdateVehicle
  }
)

function UpdateVehicle() {
  const navigate = Route.useNavigate()
  const { sites } = useCompany()
  const { vin } = Route.useParams()
  const { t } = useTranslation(['_routes', '_global'])
  const { showSuccess, showError } = useMessageSnackbar()

  const vehicleResponse = useVehicle(vin)
  // Find the site associated with the vehicle
  const vehicleSite = sites.find(site => site.id === vehicleResponse.data?.siteId)
  const currentSite = vehicleSite
    ? { id: vehicleSite.id, name: vehicleSite.name }
    : undefined

  const matchingType = VehicleTypesSchema.options.find(
    type => type.toLowerCase() === vehicleResponse?.data?.vehicleType.toLowerCase()
  )

  const form = useForm<CreateVehicleForm>({
    resolver: zodResolver(CreateVehicleFormSchema),
    defaultValues: vehicleResponse.data
      ? {
          siteId: currentSite?.id,
          vin: vehicleResponse.data.vin,
          licensePlate: vehicleResponse.data.licensePlate ?? undefined,
          vehicleName: vehicleResponse.data.name,
          vehicleType: matchingType,
          model: vehicleResponse.data.model,
          engineType: vehicleResponse.data.engineType,
          integrationPartner: vehicleResponse.data.integrationSource.type,
          integrationId: vehicleResponse.data.integrationSource.identifier,
          contract:
            vehicleResponse.data.contract == undefined
              ? { enabled: false }
              : {
                  enabled: true,
                  contractedMileage: vehicleResponse.data.contract.totalContractedMileage,
                  startDateOdometer: vehicleResponse.data.contract.startDateOdometer,
                  startDate: vehicleResponse.data.contract.startDate,
                  endDate: vehicleResponse.data.contract.endDate
                }
        }
      : undefined
  })

  const options: UseUpdateVehicleOptions = {
    onSuccess: vehicleData => {
      navigate({
        to: '/$companySlug/vehicles/$vin',
        params: { vin: vehicleData.vin }
      })
      showSuccess(
        t('routes.$fleetId_vehicles_$vehicleId_update.messages.success', {
          name: vehicleData.name
        })
      )
    },
    onError: error => {
      showError(error.message)
    }
  }
  // to do update this hook, remove fleetId
  const updateVehicleMutation = useUpdateVehicle(vin, options)

  const onSubmit = (data: CreateVehicleForm) => {
    updateVehicleMutation.mutate(data)
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, marginLeft: 0 }}>
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 4
        }}
      >
        <PageHeader
          backArrow={{
            historyBack: true,
            to: '/$companySlug/vehicles/$vin',
            from: Route.fullPath,
            params: { vin: vin }
          }}
          subtitle={t('routes.$fleetId_vehicles_$vehicleId_update.subtitle')}
        >
          {t('routes.$fleetId_vehicles_$vehicleId_update.title', {
            plateNumber: (vehicleResponse?.data?.licensePlate ?? undefined)?.replaceAll(
              '-',
              ' - '
            )
          })}
        </PageHeader>
      </Box>

      <VehicleForm
        form={form}
        onSubmit={onSubmit}
        isSubmitting={updateVehicleMutation.isPending}
        submitButtonText={t(
          'routes.$fleetId_vehicles_$vehicleId_update.actionButtonText'
        )}
        loadingButtonText={t('routes.$fleetId_vehicles_$vehicleId_update.updatingAction')}
        isCreateForm={false}
        currentSite={currentSite}
      />
    </Container>
  )
}
